import React from 'react';
import {
  ArrowRight,
  Compass,
  Wrench,
  Cpu,
  Sparkles,
  Sun,
  CheckCircle2,
  Building2,
} from 'lucide-react';
import { Navbar } from '../../components/common/Navbar';
import { Footer } from '../../components/common/Footer';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { SectionHeader } from '../../components/common/SectionHeader';
import { SEO } from '../../components/common/SEO';

export default function BusinessPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-navy">
      <SEO
        title="事業内容｜設計・施工から制御・保守まで"
        description="太陽光発電の検討から導入、現場の電力制御、保守・運用まで。Spaq株式会社が提供する一貫したエネルギー支援事業をご紹介します。"
        canonicalPath="/business"
      />
      <Navbar />

      <main className="flex-1">
        {/* Page Hero */}
        <section className="bg-hero-gradient border-b border-surface-border py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <Badge variant="cyan" className="mb-4">BUSINESS & SERVICES</Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy tracking-tight leading-tight">
              施設ごとの電気の使い方に合わせて、
              <br className="hidden sm:inline" />
              設計から運用まで。
            </h1>
            <p className="mt-5 text-base sm:text-lg text-navy-muted leading-relaxed max-w-3xl">
              SPAQは、①電力の分析・設計コンサルティング、②太陽光・蓄電池等の設備導入・施工、③導入後の電力制御・運用支援の3つの領域を一体で提供しています。部分的なご相談からワンストップ導入まで柔軟に対応します。
            </p>
          </div>
        </section>

        {/* 事業全体の構造図 */}
        <section className="py-16 md:py-20 bg-white border-b border-surface-border">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-navy">
                SPAQの3つの事業領域とプロダクトの関係
              </h2>
              <Badge variant="slate" size="sm">説明用の図</Badge>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[
                {
                  num: '領域 01',
                  title: '電力の分析・設計コンサルティング',
                  product: '関連プロダクト：1minute solar',
                  badgeVariant: 'gold' as const,
                  icon: <Compass className="w-6 h-6 text-spaq-gold-dark" />,
                  desc: '施設の電気料金・30分デマンドデータ・屋根条件を分析し、「太陽光を入れるべきか」「何kWが最も無駄がないか」を客観的に試算・設計します。',
                  items: [
                    '1minute solarによる初期相性・経済性診断',
                    '30分デマンド波形・休日ベース負荷の分析',
                    '過積載・逆潮流ロスを抑えた最適容量設計',
                  ],
                },
                {
                  num: '領域 02',
                  title: '設備の導入・調達・施工',
                  product: '自己所有・PPA・補助金活用に対応',
                  badgeVariant: 'slate' as const,
                  icon: <Wrench className="w-6 h-6 text-navy" />,
                  desc: '2,000件以上の設置・施工関与で培った技術力をもとに、太陽光パネル・PCS・産業用蓄電池・受変電連携の調達から施工・連系手続きまで一貫対応します。',
                  items: [
                    '現地調査・構造確認・レイアウト図面作成',
                    '補助金活用やPPAモデルを含む導入スキーム相談',
                    '安全・長期信頼性を重視した施工管理と連系手続き',
                  ],
                },
                {
                  num: '領域 03',
                  title: '電力制御・運用支援',
                  product: '関連プロダクト：SPAQ CORE',
                  badgeVariant: 'cyan' as const,
                  icon: <Cpu className="w-6 h-6 text-spaq-cyan-dark" />,
                  desc: '導入した太陽光・空調（デマンドコントローラー）・蓄電池を「SPAQ CORE」で連携させ、快適性を損なわずにピーク電力と電気代を継続的に抑えます。',
                  items: [
                    '太陽光自家消費＋空調＋蓄電池の協調制御',
                    '30分デマンド時限に同期したピーク抑制運用',
                    '導入後の電力モニタリング・運用チューニング',
                  ],
                },
              ].map((domain, idx) => (
                <div
                  key={idx}
                  className="rounded-3xl border border-surface-border bg-surface-slate/60 p-7 flex flex-col justify-between shadow-soft"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-mono font-bold text-navy-muted">
                        {domain.num}
                      </span>
                      <Badge variant={domain.badgeVariant} size="sm">
                        {domain.product}
                      </Badge>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-white border border-surface-border flex items-center justify-center mb-4">
                      {domain.icon}
                    </div>
                    <h3 className="text-xl font-bold text-navy mb-3">{domain.title}</h3>
                    <p className="text-sm text-navy-muted leading-relaxed mb-5">
                      {domain.desc}
                    </p>
                    <ul className="space-y-2 border-t border-surface-border pt-4">
                      {domain.items.map((pt, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs sm:text-sm text-navy">
                          <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-0.5" />
                          <span>{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* プロダクトへの詳細導線 */}
        <section className="py-16 md:py-24 bg-surface-sky border-b border-surface-border">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              tag="PRODUCTS"
              title="各プロダクトの詳細はこちら"
              subtitle="導入前の検討段階から、導入後の現場運用まで、目的に応じてご覧いただけます。"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-3xl border border-surface-border p-7 shadow-soft flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <Sun className="w-6 h-6 text-spaq-gold-dark" />
                    <h3 className="text-2xl font-bold text-navy">1minute solar</h3>
                  </div>
                  <p className="text-sm text-navy-muted leading-relaxed mb-6">
                    太陽光を導入する前に、施設の電力需要との相性や経済性を素早く確かめるシミュレーションソフトウェア。
                  </p>
                </div>
                <Button
                  to="/products/1minute-solar"
                  variant="navy"
                  icon={<ArrowRight className="w-4 h-4" />}
                >
                  1minute solar の詳細を見る
                </Button>
              </div>

              <div className="bg-white rounded-3xl border border-surface-border p-7 shadow-soft flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <Cpu className="w-6 h-6 text-spaq-cyan-dark" />
                    <h3 className="text-2xl font-bold text-navy">SPAQ CORE</h3>
                  </div>
                  <p className="text-sm text-navy-muted leading-relaxed mb-6">
                    太陽光の自家消費・空調制御（デマコン）・蓄電池を30分デマンド時限に合わせて賢く連携させる現場制御システム。
                  </p>
                </div>
                <Button
                  to="/products/spaq-core"
                  variant="primary"
                  icon={<ArrowRight className="w-4 h-4" />}
                >
                  SPAQ CORE の詳細を見る
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* 継続事業としてのクリエイティブ事業（イルミネーション等）の簡潔な案内 */}
        <section className="py-14 md:py-20 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl border border-surface-border bg-surface-slate p-7 sm:p-9 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="space-y-2 max-w-2xl">
                <div className="inline-flex items-center gap-1.5 text-xs font-bold text-spaq-gold-dark">
                  <Sparkles className="w-4 h-4" />
                  <span>継続展開事業のご案内</span>
                </div>
                <h2 className="text-xl font-bold text-navy">
                  クリエイティブ事業（商業施設・自治体向けイルミネーション企画・施工）
                </h2>
                <p className="text-xs sm:text-sm text-navy-muted leading-relaxed">
                  Spaq株式会社では、創業期より培った電気設備・LED制御・空間演出のノウハウを活かし、商業施設や駅前広場、自治体イベント等のイルミネーション企画・設計・施工事業も継続して展開しております。省電力と魅力的な空間演出を両立したご提案が可能です。
                </p>
              </div>
              <div className="shrink-0">
                <Button to="/contact" variant="outline-dark" icon={<ArrowRight className="w-4 h-4" />}>
                  事業に関するお問い合わせ
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
